import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Container,
  Alert,
  Collapse,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import Header from "./Header";
import { useParams, useLocation } from "react-router-dom";
import { allProducts, ProductInfo } from "./AllProducts";
import EditableInfo from "./EditableInfo";
import CheckoutConfirmation from "./Confirmation";
import { SERVER_BASE_URL } from "./Config";
import PatientForm from "./patient/PatientForm";
import DeliveryForm from "./DeliveryForm";
import ProviderForm from "./patient/ProviderForm";
import SearchPatient from "./patient/SearchPatient";

interface ProductDirections {
  days: string | number;
  refills: number;
  directions: string;
}

interface PatientInformation {
  email: string;
  name: string;
  dob: string;
  phone: string;
}

interface Address {
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface DeliveryInfo {
  deliveryMethod: string;
  deliveryAddress: string;
  needByDate: string;
  billingOption: string;
  specialInstructions: string;
}

interface SelectedPatient {
  id: number;
  name: string;
  email: string;
  phone: string;
  dob: string;
}

const CheckoutPage: React.FC = () => {
  let { id, strengthId } = useParams();
  const location = useLocation();
  const reorderData = (location.state as any)?.reorderData;

  const productId = id ? parseInt(id) : undefined;
  const [selectedStrengthIndex, setSelectedStrengthIndex] = useState<number>(parseInt(strengthId || "0"));

  const [deliveryInfo, setDeliveryInfo] = useState<any>(
    reorderData ? { specialInstructions: reorderData.specialInstructions || "" } : {}
  );
  const [patientId, setPatientId] = useState<number | null>(reorderData?.patient?.id || null);
  const [selectedPatient, setSelectedPatient] = useState<SelectedPatient | null>(
    reorderData?.patient ? {
      id: reorderData.patient.id,
      name: reorderData.patient.name,
      email: reorderData.patient.email,
      phone: reorderData.patient.phone,
      dob: reorderData.patient.dob,
    } : null
  );
  const [providerName, setProviderName] = useState<string>("");
  const [product, setProduct] = useState<ProductInfo>();
  const [quantity, setQuantity] = useState<number | string>(reorderData?.quantity || 1);
  const [quantityDropdown, setQuantityDropdown] = useState<string>(
    reorderData?.quantity && ["1", "2", "3", "4", "5"].includes(String(reorderData.quantity))
      ? String(reorderData.quantity)
      : reorderData?.quantity ? "other" : "1"
  );
  const [batchSize, setBatchSize] = useState<number | string>(1);
  const [batchSizeDropdown, setBatchSizeDropdown] = useState<string>("1");
  const [selectedPatients, setSelectedPatients] = useState<SelectedPatient[]>(
    reorderData?.patient ? [{
      id: reorderData.patient.id,
      name: reorderData.patient.name,
      email: reorderData.patient.email,
      phone: reorderData.patient.phone,
      dob: reorderData.patient.dob,
    }] : []
  );
  const [isShowingPatientForm, setIsShowingPatientForm] = useState<boolean>(false);
  const [editingPatientId, setEditingPatientId] = useState<number | null>(null);
  const [patientAddresses, setPatientAddresses] = useState<Record<number, any[]>>({});
  const [showBatchAddressDetails, setShowBatchAddressDetails] = useState<boolean>(false);
  const prevEditingPatientIdRef = React.useRef<number | null>(null);

  const loadProduct = async () => {
    const providerToken = localStorage.getItem("providerToken");
    const response = await fetch(`${SERVER_BASE_URL}/product/${productId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${providerToken}`, // Attach the token here
      },
    });
    const data = await response.json();
    // console.log(data);
    setProduct(data);
  };

  const loadProvider = async () => {
    try {
      const providerToken = localStorage.getItem("providerToken");
      const response = await fetch(`${SERVER_BASE_URL}/provider`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${providerToken}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setProviderName(data.name || "");
      }
    } catch (error) {
      console.error("Error fetching provider:", error);
    }
  };

  useEffect(() => {
    loadProduct();
    loadProvider();
  }, []);

  // Pre-populate directions with default directions when product is loaded (skip for reorders)
  useEffect(() => {
    if (product && product.defaultDirections && !reorderData) {
      setProductDirections(prev => ({
        ...prev,
        directions: product.defaultDirections
      }));
    }
  }, [product]);

  // Fetch addresses for all selected patients in batch mode when delivery method is shipToPatient
  useEffect(() => {
    const fetchPatientAddresses = async () => {
      if (product?.supportsBatching && deliveryInfo.deliveryMethod === "shipToPatient" && selectedPatients.length > 0) {
        const providerToken = localStorage.getItem("providerToken");
        const addressPromises = selectedPatients.map(async (patient) => {
          try {
            const response = await fetch(`${SERVER_BASE_URL}/patient_address/${patient.id}`, {
              headers: {
                Authorization: `Bearer ${providerToken}`,
              },
            });
            const data = await response.json();
            return { patientId: patient.id, addresses: data };
          } catch (error) {
            console.error(`Failed to fetch addresses for patient ${patient.id}:`, error);
            return { patientId: patient.id, addresses: [] };
          }
        });

        const results = await Promise.all(addressPromises);
        const addressMap: Record<number, any[]> = {};
        results.forEach(result => {
          addressMap[result.patientId] = result.addresses;
        });
        setPatientAddresses(addressMap);
      }
    };

    fetchPatientAddresses();
  }, [product?.supportsBatching, deliveryInfo.deliveryMethod, selectedPatients]);

  // Clear delivery location when selected patients change in batch mode
  useEffect(() => {
    if (product?.supportsBatching && deliveryInfo.deliveryMethod === "shipToPatient") {
      // Reset delivery location selection when patients change
      setDeliveryInfo((prev: any) => ({
        ...prev,
        patientAddressId: null,
        addressString: ""
      }));
    }
  }, [selectedPatients.length, product?.supportsBatching]);

  // const productInfo = allProducts.find((product) => product.id === productId);

  // const [product, setProduct] = useState<Product>({
  //   id: productId,
  //   name: productInfo?.name || "Product",
  //   price: 10,
  //   strength: 3,
  //   // price: productInfo?.price || 10,
  //   // strength: productInfo?.strength || 3,
  //   image: productInfo?.imageUrl || "",
  //   quantity: 1
  // });

  const savedPrefs = JSON.parse(localStorage.getItem("checkoutPrefs") || "{}");

  const [productDirections, setProductDirections] = useState<ProductDirections>(
    {
      days: reorderData?.daysOfSupply?.toString() || "28",
      refills: reorderData?.refills ?? 0,
      directions: reorderData?.directions || "",
    },
  );

  // const [address, setAddress] = useState<Address>({
  //   street: "",
  //   city: "",
  //   state: "",
  //   zip: ""
  // });
  //
  // const [patientInfo, setPatientInfo] = useState<PatientInformation>({
  //   email: "",
  //   name: "",
  //   dob: "",
  //   phone: ""
  // });

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // if (!productInfo) {
  //   return <div>Product not found!</div>;
  // }

  const handleQuantityChange = (quantity: number | string) => {
    setQuantity(quantity);
    // setProduct({ ...product, quantity });
  };

  const handleQuantityDropdownChange = (value: string) => {
    setQuantityDropdown(value);
    if (value === "other") {
      setQuantity("custom");
    } else {
      setQuantity(parseInt(value));
    }
  };

  const handleCustomQuantityChange = (value: string) => {
    const numValue = parseInt(value);
    if (value === "" || (!isNaN(numValue) && numValue > 0)) {
      setQuantity(value === "" ? "" : numValue);
    }
  };

  // Helper function to get numeric quantity for calculations
  const getNumericQuantity = (): number => {
    if (typeof quantity === 'number') return quantity;
    if (typeof quantity === 'string' && quantity !== '' && quantity !== 'custom') {
      const parsed = parseInt(quantity);
      return isNaN(parsed) ? 1 : parsed;
    }
    return 1;
  };

  // Batch size handling functions
  const handleBatchSizeDropdownChange = (value: string) => {
    setBatchSizeDropdown(value);
    if (value === "other") {
      setBatchSize("custom");
    } else {
      setBatchSize(parseInt(value));
    }
  };

  const handleCustomBatchSizeChange = (value: string) => {
    const numValue = parseInt(value);
    if (value === "" || (!isNaN(numValue) && numValue > 0)) {
      setBatchSize(value === "" ? "" : numValue);
    }
  };

  const getNumericBatchSize = (): number => {
    if (typeof batchSize === 'number') return batchSize;
    if (typeof batchSize === 'string' && batchSize !== '' && batchSize !== 'custom') {
      const parsed = parseInt(batchSize);
      return isNaN(parsed) ? 1 : parsed;
    }
    return 1;
  };

  // Patient management functions
  const addPatient = (patient: SelectedPatient) => {
    setSelectedPatients(prev => [...prev, patient]);
  };

  const removePatient = (patientId: number) => {
    setSelectedPatients(prev => prev.filter(p => p.id !== patientId));
  };

  const editPatient = (patientId: number) => {
    // Reset to null first to ensure useEffect in PatientForm triggers even for same patient
    setEditingPatientId(null);
    // Use setTimeout to ensure state update completes before setting new value
    setTimeout(() => {
      setEditingPatientId(patientId);
      setIsShowingPatientForm(true);
    }, 0);
  };

  const updatePatient = (updatedPatient: SelectedPatient) => {
    setSelectedPatients(prev =>
      prev.map(p => p.id === updatedPatient.id ? updatedPatient : p)
    );
    setEditingPatientId(null);
    setIsShowingPatientForm(false);

    // Refetch addresses for the updated patient
    refetchPatientAddresses(updatedPatient.id);
  };

  const refetchPatientAddresses = async (patientId: number) => {
    const providerToken = localStorage.getItem("providerToken");
    try {
      const response = await fetch(`${SERVER_BASE_URL}/patient_address/${patientId}`, {
        headers: {
          Authorization: `Bearer ${providerToken}`,
        },
      });
      const data = await response.json();
      setPatientAddresses(prev => ({
        ...prev,
        [patientId]: data
      }));
    } catch (error) {
      console.error(`Failed to fetch addresses for patient ${patientId}:`, error);
    }
  };

  // Refetch addresses when edit modal closes
  useEffect(() => {
    if (prevEditingPatientIdRef.current !== null && editingPatientId === null) {
      // Modal was closed, refetch addresses for the patient that was being edited
      refetchPatientAddresses(prevEditingPatientIdRef.current);
    }

    prevEditingPatientIdRef.current = editingPatientId;
  }, [editingPatientId]);

  // Fetch addresses when patient is selected in non-batch mode
  useEffect(() => {
    if (!product?.supportsBatching && patientId) {
      refetchPatientAddresses(patientId);
    }
  }, [patientId, product?.supportsBatching]);

  // Helper function to check batch address validation
  const getBatchAddressValidation = () => {
    const patientsWithDefaultAddress: SelectedPatient[] = [];
    const patientsWithoutDefaultAddress: SelectedPatient[] = [];

    selectedPatients.forEach(patient => {
      const addresses = patientAddresses[patient.id] || [];
      const hasDefaultAddress = addresses.some(addr => addr.isDefault);
      if (hasDefaultAddress) {
        patientsWithDefaultAddress.push(patient);
      } else {
        patientsWithoutDefaultAddress.push(patient);
      }
    });

    return {
      patientsWithDefaultAddress,
      patientsWithoutDefaultAddress,
      allHaveDefaultAddress: patientsWithoutDefaultAddress.length === 0,
    };
  };

  const onConfirm = async () => {
    setError("");
    setIsLoading(true);

    // Validation for batching vs regular products
    if (product?.supportsBatching) {
      // For batching products, validate batch requirements
      if (selectedPatients.length === 0) {
        setError("Please select at least one patient for the batch.");
        setIsLoading(false);
        return;
      }
      
      if (selectedPatients.length !== getNumericBatchSize()) {
        setError(`You need to select exactly ${getNumericBatchSize()} patient${getNumericBatchSize() !== 1 ? 's' : ''} for this batch size.`);
        setIsLoading(false);
        return;
      }
    } else {
      // For regular products, validate single patient
      if (!patientId) {
        setError("Please create or select a patient before continuing.");
        setIsLoading(false);
        return;
      }
    }

    // Delivery method validation
    if (!deliveryInfo.deliveryMethod) {
      setError("Please select a delivery method.");
      setIsLoading(false);
      return;
    }

    // Address validation based on delivery method
    if (deliveryInfo.deliveryMethod === "pickup" && !deliveryInfo.pickupAddressId) {
      setError("Please select a pickup location.");
      setIsLoading(false);
      return;
    }

    if (deliveryInfo.deliveryMethod === "shipToPatient") {
      if (product?.supportsBatching) {
        // For batch mode, check that all patients have default addresses
        const validation = getBatchAddressValidation();
        if (!validation.allHaveDefaultAddress) {
          setError(`Cannot proceed: ${validation.patientsWithoutDefaultAddress.length} patient${validation.patientsWithoutDefaultAddress.length !== 1 ? 's' : ''} missing default address.`);
          setIsLoading(false);
          return;
        }
      } else {
        // For regular mode, check patient address is selected
        if (!deliveryInfo.patientAddressId) {
          setError("Please select a patient delivery address.");
          setIsLoading(false);
          return;
        }
      }
    }

    if (deliveryInfo.deliveryMethod === "shipToProvider" && !deliveryInfo.providerAddressId) {
      setError("Please select a provider delivery address.");
      setIsLoading(false);
      return;
    }

    // Need by date validation
    if (!deliveryInfo.needByDate) {
      setError("Please specify a need by date.");
      setIsLoading(false);
      return;
    }

    // Billing option validation
    if (!deliveryInfo.billingOption) {
      setError("Please select a billing option.");
      setIsLoading(false);
      return;
    }


    if (!productDirections.directions) {
      setError("Please provide prescription directions.");
      setIsLoading(false);
      return;
    }

    if (!productDirections.days || productDirections.days <= 0) {
      setError("Please specify the days of supply.");
      setIsLoading(false);
      return;
    }

    try {
      const providerToken = localStorage.getItem("providerToken");
      
      if (product?.supportsBatching) {
        // For batching products, create one order per patient
        const orderPromises = selectedPatients.map(async (patient) => {
          // For shipToPatient delivery, honor the address the provider picked when it
          // belongs to this patient; otherwise fall back to the patient's default address.
          let patientDeliveryInfo = { ...deliveryInfo };
          if (deliveryInfo.deliveryMethod === "shipToPatient") {
            const addresses = patientAddresses[patient.id] || [];
            const selectedAddress = addresses.find(addr => addr.id === deliveryInfo.patientAddressId);
            const targetAddress = selectedAddress || addresses.find(addr => addr.isDefault);
            if (targetAddress) {
              patientDeliveryInfo = {
                ...deliveryInfo,
                patientAddressId: targetAddress.id,
                addressString: `${targetAddress.street}, ${targetAddress.city}, ${targetAddress.state} ${targetAddress.zip}`,
              };
            }
          }

          const batchCount = getNumericBatchSize();
          const batchNote = `This batch size has ${batchCount} Rxs`;
          const existingInstructions = patientDeliveryInfo.specialInstructions || "";
          const combinedInstructions = existingInstructions
            ? `${existingInstructions}\n${batchNote}`
            : batchNote;

          const body = {
            productId: productId,
            providerId: 1, // TODO: Get provider ID from session
            quantity: 1, // Always 1 for batching orders
            patientId: patient.id,
            deliveryInfo: {
              ...patientDeliveryInfo,
              specialInstructions: combinedInstructions,
              deliveryType: patientDeliveryInfo.deliveryMethod, // Map deliveryMethod to deliveryType for server
              needBy: patientDeliveryInfo.needByDate, // Map needByDate to needBy for server
            },
            productDirections: {
              ...productDirections,
              daysOfSupply: productDirections.days, // Map days to daysOfSupply for server
            },
            strengthIdx: selectedStrengthIndex,
          };

          return fetch(`${SERVER_BASE_URL}/place_order`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${providerToken}`,
            },
            body: JSON.stringify(body),
          });
        });

        // Wait for all orders to complete
        const responses = await Promise.all(orderPromises);
        
        // Check if all orders were successful
        const failedOrders = responses.filter(response => !response.ok);
        if (failedOrders.length > 0) {
          setError(`Failed to place ${failedOrders.length} out of ${responses.length} orders. Please try again.`);
          setIsLoading(false);
          return;
        }

        setSubmitted(true);
        setError("");
        localStorage.setItem("checkoutPrefs", JSON.stringify({
          days: productDirections.days,
          deliveryMethod: deliveryInfo.deliveryMethod,
          billingOption: deliveryInfo.billingOption,
        }));
      } else {
        // For regular products, create single order
        const body = {
          productId: productId,
          providerId: 1, // TODO: Get provider ID from session
          quantity: getNumericQuantity(),
          patientId: patientId,
          deliveryInfo: {
            ...deliveryInfo,
            deliveryType: deliveryInfo.deliveryMethod, // Map deliveryMethod to deliveryType for server
            needBy: deliveryInfo.needByDate, // Map needByDate to needBy for server
          },
          productDirections: {
            ...productDirections,
            daysOfSupply: productDirections.days, // Map days to daysOfSupply for server
          },
          strengthIdx: selectedStrengthIndex,
        };

        const response = await fetch(`${SERVER_BASE_URL}/place_order`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${providerToken}`,
          },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          setSubmitted(true);
          setError("");
          localStorage.setItem("checkoutPrefs", JSON.stringify({
            days: productDirections.days,
            deliveryMethod: deliveryInfo.deliveryMethod,
            billingOption: deliveryInfo.billingOption,
          }));
        } else {
          let errorMessage = `Failed to place order (${response.status})`;
        try {
          const errorData = await response.json();
          // Try different common error message fields
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } catch (jsonError) {
          // If JSON parsing fails, try to get text response
          try {
            const textResponse = await response.text();
            if (textResponse) {
              errorMessage = textResponse;
            }
          } catch (textError) {
            // Keep the default error message
          }
        }
        setError(errorMessage);
        }
      }
    } catch (err) {
      setError("Network error occurred. Please check your connection and try again.");
      console.error("Checkout error:", err);
    } finally {
      setIsLoading(false);
    }
    // const data = await response.json();

    // fetch("/api/prescriptions", {
    //   method: "POST",
    //   headers: {
    //     "Content-Type": "application/json"
    //   },
    //   body: JSON.stringify(body)
    // })
    //   .then((res) => res.json())
    //   .then((data) => {
    //     console.log(data);
    //     setSubmitted(true);
    //   })
    //   .catch((err) => console.error(err));
  };

  if (!product) {
    return <div>Loading...</div>;
  }


  return (
    <Box sx={{ display: "flex", flexDirection: "column", overflowX: "hidden" }}>
      {/* Persistent Spacer Box */}
      <Box sx={{ height: { xs: "8px", sm: "20px" } }} />
      {/* Adjust height to control spacing below the header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          p: { xs: 1, sm: 2 },
          paddingTop: { xs: "8px", sm: "20px" },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
            }}
          >
            {/*<Header></Header>*/}
            {!submitted ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: { xs: 0, sm: 3, md: 4 },
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    padding: { xs: "8px", sm: 0 },
                    top: "80px", // Ensure same distance as the right side
                  }}
                >
                  <Paper
                    sx={{
                      marginBottom: { xs: "12px", sm: "20px" },
                      padding: { xs: "12px 0", sm: "20px" },
                      width: "100%",
                      border: { xs: "none", sm: "1px solid #ccc" },
                      borderBottom: { xs: "1px solid #eee", sm: "1px solid #ccc" },
                    }}
                    elevation={0} // Removes the drop shadow
                  >
                    <Typography variant="h5" align="left" marginBottom="10px">
                      Prescription Drug
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexDirection: { xs: "column", md: "row" },
                      }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 120, md: 180 },
                            height: { xs: 120, md: 180 },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5',
                            borderRadius: 1,
                            overflow: 'hidden',
                            marginLeft: { xs: 0, md: "20px" },
                            marginRight: { xs: "16px", md: "60px" },
                          }}
                        >
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          flexDirection: "column",
                          width: { xs: "100%", md: "50%" },
                          marginRight: { xs: 0, md: "20px" },
                          mt: { xs: 2, md: 0 },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          align="left"
                        >
                          {product.name}
                        </Typography>
                        {product.strengths.length > 1 ? (
                          <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
                            <InputLabel shrink>Strength</InputLabel>
                            <Select
                              value={selectedStrengthIndex}
                              onChange={(e) => setSelectedStrengthIndex(Number(e.target.value))}
                              label="Strength"
                            >
                              {product.strengths.map((s, idx) => (
                                <MenuItem key={idx} value={idx}>
                                  {[s.strength1, s.strength2, s.dosage, s.size]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <>
                            {product.strengths[selectedStrengthIndex].strength1 && (
                              <Typography variant="subtitle2" align="left">
                                Strength: {product.strengths[selectedStrengthIndex].strength1}
                              </Typography>
                            )}
                            {product.strengths[selectedStrengthIndex].dosage && (
                              <Typography variant="subtitle2" align="left">
                                Dosage: {product.strengths[selectedStrengthIndex].dosage}
                              </Typography>
                            )}
                          </>
                        )}
                        <Box sx={{
                          width: { xs: "100%", md: "50%" },
                          marginTop: "auto",
                          display: "flex",
                          flexDirection: "column",
                          alignSelf: "flex-start",
                        }}>
                          {product?.supportsBatching ? (
                            <>
                              <FormControl fullWidth>
                                <InputLabel shrink>Batch Size</InputLabel>
                                <Select
                                  value={batchSizeDropdown}
                                  onChange={(e) => handleBatchSizeDropdownChange(e.target.value)}
                                  label="Batch Size"
                                  inputProps={{
                                    "aria-label": "Batch Size",
                                  }}
                                >
                                  {["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"].map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                  <MenuItem value="other">Other</MenuItem>
                                </Select>
                              </FormControl>
                              {(batchSizeDropdown === "other" || (batchSize && !["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"].includes(String(batchSize)))) && (
                                <TextField
                                  label="Batch Size (Other)"
                                  variant="outlined"
                                  fullWidth
                                  type="number"
                                  value={batchSize === "custom" ? "" : batchSize}
                                  onChange={(e) => handleCustomBatchSizeChange(e.target.value)}
                                  required
                                  placeholder="Enter custom batch size"
                                  inputProps={{ min: 1, step: 1 }}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </>
                          ) : (
                            <>
                              <FormControl fullWidth>
                                <InputLabel shrink>Quantity</InputLabel>
                                <Select
                                  value={quantityDropdown}
                                  onChange={(e) => handleQuantityDropdownChange(e.target.value)}
                                  label="Quantity"
                                  inputProps={{
                                    "aria-label": "Quantity",
                                  }}
                                >
                                  {["1", "2", "3", "4", "5"].map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                  <MenuItem value="other">Other</MenuItem>
                                </Select>
                              </FormControl>
                              {(quantityDropdown === "other" || (quantity && !["1", "2", "3", "4", "5"].includes(String(quantity)))) && (
                                <TextField
                                  label="Quantity (Other)"
                                  variant="outlined"
                                  fullWidth
                                  type="number"
                                  value={quantity === "custom" ? "" : quantity}
                                  onChange={(e) => handleCustomQuantityChange(e.target.value)}
                                  required
                                  placeholder="Enter custom quantity"
                                  inputProps={{ min: 1, step: 1 }}
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Typography variant="body1" align="left" sx={{ marginLeft: { xs: 0, md: "20px" }, mt: { xs: 1, md: 3 } }}>
                      <b>Administration Method:</b>{" "}
                      {product.administrationMethod}
                    </Typography>
                    <Typography variant="body1" align="left" sx={{ marginLeft: { xs: 0, md: "20px" } }}>
                      <b>Dosage Form:</b> {product.dosageForm}
                    </Typography>
                  </Paper>

                  <Grid container spacing={2}>
                    {" "}
                    {/* spacing={2} adds some gap */}
                    <Grid item xs={12}>
                      <EditableInfo<ProductDirections>
                        label="Prescription Details"
                        data={productDirections}
                        fields={{
                          days: { 
                            label: "Days of Supply", 
                            type: "number", 
                            required: true,
                            defaultValue: "28"
                          },
                          refills: { label: "Refills", type: "number", required: true },
                          directions: { label: "Directions", type: "textarea", required: true },
                          // phone: { label: "Phone", type: "phone" },
                          // date: { label: "Date", type: "date" }
                        }}
                        onSave={setProductDirections}
                        initialEditing={true}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Paper
                        sx={{ padding: { xs: "12px 0", sm: 2 }, border: { xs: "none", sm: "1px solid #ccc" }, borderBottom: { xs: "1px solid #eee", sm: "1px solid #ccc" } }}
                        elevation={0} // Removes the drop shadow
                      >
                        <Typography variant="h5" align="left" marginBottom="20px">
                          {product?.supportsBatching ? "Select Patients for Batch" : "Patient Information"}
                        </Typography>
                        
                        {product?.supportsBatching ? (
                          <>
                            {/* Validation message for batch size */}
                            {selectedPatients.length !== getNumericBatchSize() && (
                              <Alert severity="warning" sx={{ mb: 2 }}>
                                You need to select exactly {getNumericBatchSize()} patient{getNumericBatchSize() !== 1 ? 's' : ''} for this batch size.
                                Currently selected: {selectedPatients.length}
                              </Alert>
                            )}
                            
                            {/* Selected patients list */}
                            {selectedPatients.length > 0 && (
                              <Box>
                                <List sx={{ py: 0 }}>
                                  {selectedPatients.map((patient) => (
                                    <ListItem
                                      key={patient.id}
                                      sx={{
                                        border: '1px solid #ddd',
                                        mb: 1,
                                        borderRadius: 1,
                                        pr: 12
                                      }}
                                    >
                                      <ListItemText
                                        primary={patient.name}
                                        secondary={
                                          <>
                                            {patient.email}<br />
                                            {patient.phone}<br />
                                            DOB: {patient.dob}
                                          </>
                                        }
                                        sx={{
                                          '& .MuiListItemText-secondary': {
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word'
                                          }
                                        }}
                                      />
                                      <ListItemSecondaryAction>
                                        <IconButton
                                          onClick={() => editPatient(patient.id)}
                                          color="primary"
                                          sx={{ mr: 1 }}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                        <IconButton
                                          edge="end"
                                          onClick={() => removePatient(patient.id)}
                                          color="error"
                                        >
                                          <CloseIcon />
                                        </IconButton>
                                      </ListItemSecondaryAction>
                                    </ListItem>
                                  ))}
                                </List>
                              </Box>
                            )}
                            
                            {/* Patient search and selection */}
                            {selectedPatients.length < getNumericBatchSize() && (
                              <Box sx={{ mb: 2 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>Search and Select Patients:</Typography>
                                <SearchPatient
                                  excludePatientIds={selectedPatients.map(p => p.id)}
                                  onSelect={(patient) => {
                                    // Check if patient is already selected and batch isn't full
                                    if (!selectedPatients.find(p => p.id === patient.id) && selectedPatients.length < getNumericBatchSize()) {
                                      addPatient({
                                        id: patient.id,
                                        name: patient.name,
                                        email: patient.email,
                                        phone: patient.phone,
                                        dob: patient.dob
                                      });
                                    }
                                  }}
                                />

                                {/* Simple add new patient form */}
                                {!isShowingPatientForm && (
                                  <Button
                                    variant="outlined"
                                    onClick={() => setIsShowingPatientForm(true)}
                                    sx={{ mt: 2 }}
                                  >
                                    + Add New Patient
                                  </Button>
                                )}

                                {isShowingPatientForm && (
                                  <Box sx={{ border: '1px solid #ddd', p: 2, borderRadius: 1, backgroundColor: '#f9f9f9', mt: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                      Add New Patient
                                    </Typography>
                                    <PatientForm
                                      updatePatientId={(id) => {
                                        console.log('Patient created/updated with ID:', id);
                                        setIsShowingPatientForm(false);
                                        setEditingPatientId(null);
                                      }}
                                      onPatientSaved={(patient) => {
                                        // Add new patient to selected patients list if not already there and batch isn't full
                                        if (!selectedPatients.find(p => p.id === patient.id) && selectedPatients.length < getNumericBatchSize()) {
                                          addPatient(patient);
                                        }
                                      }}
                                      onAddressesChange={(patientId) => {
                                        // Refetch addresses when patient addresses are modified
                                        refetchPatientAddresses(patientId);
                                      }}
                                    />
                                    <Button
                                      variant="text"
                                      onClick={() => {
                                        setIsShowingPatientForm(false);
                                        setEditingPatientId(null);
                                      }}
                                      sx={{ mt: 1 }}
                                    >
                                      Cancel
                                    </Button>
                                  </Box>
                                )}
                              </Box>
                            )}

                            {/* Separate PatientForm for editing - only shows modal, no inline form */}
                            {editingPatientId && (
                              <PatientForm
                                updatePatientId={(id) => {
                                  console.log('Patient updated with ID:', id);
                                  setEditingPatientId(null);
                                }}
                                onPatientSaved={(patient) => {
                                  // Update existing patient in selected patients list
                                  updatePatient(patient);
                                }}
                                onAddressesChange={(patientId) => {
                                  // Refetch addresses when patient addresses are modified
                                  refetchPatientAddresses(patientId);
                                }}
                                editingPatient={selectedPatients.find(p => p.id === editingPatientId)}
                                modalOnly={true}
                              />
                            )}
                          </>
                        ) : (
                          <PatientForm
                            updatePatientId={setPatientId}
                            onPatientSaved={(patient) => {
                              // Store patient info and refetch addresses when patient is created/updated
                              setSelectedPatient(patient);
                              if (patient.id) {
                                refetchPatientAddresses(patient.id);
                              }
                            }}
                            onAddressesChange={(patientId) => {
                              // Refetch addresses when patient addresses are modified
                              refetchPatientAddresses(patientId);
                            }}
                            initialPatient={reorderData?.patient}
                          />
                        )}
                      </Paper>
                    </Grid>
                    {/*<Grid item xs={12}>*/}
                    {/*  <Paper sx={{ padding: 2 }}>*/}
                    {/*    <h3>Provider Info</h3>*/}
                    {/*    <ProviderForm></ProviderForm>*/}
                    {/*  </Paper>*/}
                    {/*</Grid>*/}
                    <Grid item xs={12}>
                      <Paper
                        sx={{ padding: { xs: "12px 0", sm: 2 }, border: { xs: "none", sm: "1px solid #ccc" }, borderBottom: { xs: "1px solid #eee", sm: "1px solid #ccc" } }}
                        elevation={0} // Removes the drop shadow
                      >
                        <Typography
                          variant="h5"
                          align="left"
                          marginBottom="20px"
                        >
                          Delivery Information
                        </Typography>
                        {product.turnaroundTime && (
                          <Typography variant="body1" align="left" marginBottom="16px">
                            <b>Expected Turnaround:</b> {product.turnaroundTime}
                          </Typography>
                        )}

                        {/* Batch address validation UI for shipToPatient (batch size > 1) */}
                        {product?.supportsBatching && deliveryInfo.deliveryMethod === "shipToPatient" && selectedPatients.length > 0 && getNumericBatchSize() > 1 && (
                          <Box sx={{ mb: 2 }}>
                            {(() => {
                              const validation = getBatchAddressValidation();

                              if (validation.allHaveDefaultAddress) {
                                return (
                                  <Alert severity="success" sx={{ mb: 2 }}>
                                    All {selectedPatients.length} patient{selectedPatients.length !== 1 ? 's' : ''} have default shipping addresses configured.
                                  </Alert>
                                );
                              } else {
                                return (
                                  <>
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                      {validation.patientsWithoutDefaultAddress.length} patient{validation.patientsWithoutDefaultAddress.length !== 1 ? 's' : ''} missing default shipping address.
                                      <Button
                                        size="small"
                                        onClick={() => setShowBatchAddressDetails(!showBatchAddressDetails)}
                                        sx={{ ml: 1 }}
                                      >
                                        {showBatchAddressDetails ? 'Hide Details' : 'Show Details'}
                                      </Button>
                                    </Alert>

                                    <Collapse in={showBatchAddressDetails}>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                          Patients needing default address:
                                        </Typography>
                                        <List dense>
                                          {validation.patientsWithoutDefaultAddress.map(patient => {
                                            const addresses = patientAddresses[patient.id] || [];
                                            return (
                                              <ListItem key={patient.id} sx={{ display: 'block', mb: 2, border: '1px solid #ffcdd2', borderRadius: 1, p: 2 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                  {patient.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                  {patient.email} • {patient.phone}
                                                </Typography>
                                                {addresses.length > 0 ? (
                                                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                                                    Has {addresses.length} address{addresses.length !== 1 ? 'es' : ''} but none marked as default.
                                                    Please edit patient to set a default address.
                                                  </Typography>
                                                ) : (
                                                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                                    No addresses on file. Please edit patient to add an address.
                                                  </Typography>
                                                )}
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  color="primary"
                                                  onClick={() => editPatient(patient.id)}
                                                  sx={{ mt: 1 }}
                                                >
                                                  Edit Patient & Add/Set Default Address
                                                </Button>
                                              </ListItem>
                                            );
                                          })}
                                        </List>
                                      </Box>
                                    </Collapse>
                                  </>
                                );
                              }
                            })()}
                          </Box>
                        )}

                        {/* Address validation for batch size = 1 with shipToPatient */}
                        {product?.supportsBatching && deliveryInfo.deliveryMethod === "shipToPatient" && selectedPatients.length > 0 && getNumericBatchSize() === 1 && (
                          <Box sx={{ mb: 2 }}>
                            {(() => {
                              const patient = selectedPatients[0];
                              const addresses = patientAddresses[patient.id] || [];
                              const hasDefaultAddress = addresses.some(addr => addr.isDefault);

                              if (!hasDefaultAddress) {
                                return (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    Patient is missing a default shipping address.
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => editPatient(patient.id)}
                                      sx={{ ml: 1 }}
                                    >
                                      Add Address
                                    </Button>
                                  </Alert>
                                );
                              }
                              return null;
                            })()}
                          </Box>
                        )}

                        {/* Address validation for non-batch mode with shipToPatient */}
                        {!product?.supportsBatching && deliveryInfo.deliveryMethod === "shipToPatient" && patientId && (
                          <Box sx={{ mb: 2 }}>
                            {(() => {
                              const addresses = patientAddresses[patientId] || [];
                              const hasDefaultAddress = addresses.some(addr => addr.isDefault);

                              if (!hasDefaultAddress) {
                                return (
                                  <Alert severity="error" sx={{ mb: 2 }}>
                                    Patient is missing a default shipping address. Please add an address in the Patient Information section above.
                                  </Alert>
                                );
                              }
                              return null;
                            })()}
                          </Box>
                        )}

                        <DeliveryForm
                          patientId={product?.supportsBatching && getNumericBatchSize() === 1 ? selectedPatients[0]?.id : patientId}
                          onDeliveryInfoChange={setDeliveryInfo}
                          supportsBatching={product?.supportsBatching}
                          patientAddresses={
                            product?.supportsBatching && getNumericBatchSize() === 1 && selectedPatients[0]?.id
                              ? patientAddresses[selectedPatients[0].id]
                              : patientId
                                ? patientAddresses[patientId]
                                : undefined
                          }
                          initialDeliveryMethod={reorderData?.deliveryMethod || savedPrefs.deliveryMethod}
                          initialBillingOption={reorderData?.billingOption || savedPrefs.billingOption}
                          initialSpecialInstructions={reorderData?.specialInstructions}
                          initialPatientAddressId={reorderData?.patientAddressId}
                          initialProviderAddressId={reorderData?.providerAddressId}
                        ></DeliveryForm>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper
                        sx={{ padding: { xs: "12px 0", sm: 2 }, border: { xs: "none", sm: "1px solid #ccc" }, borderBottom: { xs: "1px solid #eee", sm: "1px solid #ccc" } }}
                        elevation={0}
                      >
                        <Typography variant="h5" align="left" marginBottom="20px">
                          Order Special Instructions
                        </Typography>
                        <TextField
                          label="Order Special Instructions"
                          fullWidth
                          multiline
                          rows={3}
                          value={deliveryInfo.specialInstructions}
                          onChange={(e) => setDeliveryInfo({ ...deliveryInfo, specialInstructions: e.target.value })}
                        />
                      </Paper>
                    </Grid>
                  </Grid>

                  {/*<EditableInfo<PatientInformation>*/}
                  {/*  label="Patient Information"*/}
                  {/*  data={patientInfo}*/}
                  {/*  fields={{*/}
                  {/*    email: { label: "Email", type: "text" },*/}
                  {/*    name: { label: "Name", type: "text" },*/}
                  {/*    dob: { label: "Date of Birth", type: "date" },*/}
                  {/*    phone: { label: "Phone", type: "phone" }*/}
                  {/*  }}*/}
                  {/*  onSave={setPatientInfo}*/}
                  {/*  initialEditing={true}*/}
                  {/*/>*/}

                  {/*// Example usage for Address*/}
                  {/*<EditableInfo<Address>*/}
                  {/*  label="Address"*/}
                  {/*  data={address}*/}
                  {/*  fields={{*/}
                  {/*    street: { label: "Street", type: "text" },*/}
                  {/*    city: { label: "City", type: "text" },*/}
                  {/*    state: { label: "State", type: "text" },*/}
                  {/*    zip: { label: "ZIP", type: "text" }*/}
                  {/*  }}*/}
                  {/*  onSave={setAddress}*/}
                  {/*  initialEditing={true}*/}
                  {/*/>*/}

                  {/*<Button*/}
                  {/*  variant="contained"*/}
                  {/*  color="primary"*/}
                  {/*  style={{ width: "70%", borderRadius: "20px", marginTop: "20px" }}*/}
                  {/*  onClick={onConfirm}*/}
                  {/*>*/}
                  {/*  Confirm*/}
                  {/*</Button>*/}
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    padding: { xs: 0, sm: 0 },
                    alignSelf: { xs: "stretch", md: "flex-start" },
                  }}
                >
                  <Paper
                    sx={{
                      padding: { xs: "12px 0", sm: "20px" },
                      width: "100%",
                      position: { xs: "static", md: "sticky" },
                      top: { md: "100px" },
                      zIndex: { md: 100 },
                      border: { xs: "none", sm: "1px solid #ccc" },
                    }}
                    elevation={0} // Removes the drop shadow
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="h5" align="left">
                        Prescription Summary
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography
                        variant="subtitle2"
                        align="left"
                        marginTop="15px"
                      >
                        {product.name}
                      </Typography>
                      {product.strengths[selectedStrengthIndex].strength1 && (
                        <Typography variant="subtitle2" align="left">
                          Strength: {product.strengths[selectedStrengthIndex].strength1}
                        </Typography>
                      )}
                      {product.strengths[selectedStrengthIndex].strength2 && (
                        <Typography variant="subtitle2" align="left">
                          {product.strengths[selectedStrengthIndex].strength2}
                        </Typography>
                      )}
                      {product.strengths[selectedStrengthIndex].dosage && (
                        <Typography variant="subtitle2" align="left">
                          Dosage: {product.strengths[selectedStrengthIndex].dosage}
                        </Typography>
                      )}
                      {product.strengths[selectedStrengthIndex].duration && (
                        <Typography variant="subtitle2" align="left">
                          Duration: {product.strengths[selectedStrengthIndex].duration}
                        </Typography>
                      )}
                      {product.strengths[selectedStrengthIndex].size && (
                        <Typography variant="subtitle2" align="left">
                          Size: {product.strengths[selectedStrengthIndex].size}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", mt: "15px", gap: 0.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Typography variant="h5">
                                Retail Price
                              </Typography>
                              <Tooltip title="This is a suggested retail price. Actual cost may vary based on insurance coverage, discount programs, or other pricing arrangements." enterTouchDelay={0} leaveTouchDelay={3000}>
                                <IconButton size="small" sx={{ ml: 1 }}>
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Typography variant="h5">
                              ${((product.strengths[selectedStrengthIndex]["price"] / 100) * getNumericQuantity()).toFixed(2)}
                            </Typography>
                          </Box>
                          {product.strengths[selectedStrengthIndex]["provider_price"] != null && (
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <Typography variant="body1" color="text.secondary">
                                Provider Price
                              </Typography>
                              <Typography variant="body1" color="text.secondary">
                                ${((product.strengths[selectedStrengthIndex]["provider_price"]! / 100) * getNumericQuantity()).toFixed(2)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", mt: "20px", mb: { xs: 5, sm: 0 } }}>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isLoading}
                      sx={{
                        width: { xs: "90%", sm: "70%" },
                        borderRadius: "20px",
                      }}
                      onClick={onConfirm}
                    >
                      {isLoading ? "Processing..." : "Confirm"}
                    </Button>
                    </Box>

                    {/* Error Message */}
                    <Collapse in={!!error}>
                      <Alert 
                        severity="error" 
                        onClose={() => setError("")}
                        sx={{ mt: 2 }}
                      >
                        {error}
                      </Alert>
                    </Collapse>
                  </Paper>
                </Box>
              </Box>
            ) : (
              <CheckoutConfirmation
                product={product.name}
                address={deliveryInfo["addressString"]}
                quantity={product?.supportsBatching ? 1 : getNumericQuantity()}
                deliveryInfo={deliveryInfo}
                productDirections={productDirections}
                dosage={product.strengths[selectedStrengthIndex].dosage}
                duration={product.strengths[selectedStrengthIndex].duration}
                patients={product?.supportsBatching ? selectedPatients : (selectedPatient ? [selectedPatient] : undefined)}
                strength={product.strengths[selectedStrengthIndex].strength1}
                size={product.strengths[selectedStrengthIndex].size}
                billingOption={deliveryInfo.billingOption}
                providerName={providerName}
              ></CheckoutConfirmation>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default CheckoutPage;
